// PERSONA — componente CANÓNICO ÚNICO (mismo contrato para Central, Radar y deep-link).
// Orden obligatorio: IDENTIDAD → ATENCIÓN → COBERTURA (antes que observable) → OBSERVABLE →
// ÚLTIMOS HECHOS → CONTACTOS. No interpreta clínicamente, no inventa identidad ni bienestar.
import { COBERTURA_LABEL, COBERTURA_TONE } from "@/lib/persona/contract";
import { acentoPersona } from "@/lib/consolaV3/meta";
import {
  BloqueContexto, BloquePlan, BloqueContactos, BloqueEquipos, BloqueEjes, etiquetaModalidad,
} from "@/components/persona/BloquesOperativos";
import {
  User, MapPin, ShieldCheck, ShieldAlert, ShieldOff, Activity, Radio, HeartPulse,
  Clock, Phone, History, PlusCircle, UsersRound,
} from "lucide-react";

const TONE = {
  ok: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warn: "text-[#B45309] bg-[#D97706]/10 border-[#D97706]/30",
  bad: "text-[#9B2C2C] bg-[#9B2C2C]/10 border-[#9B2C2C]/30",
  muted: "text-slate-500 bg-slate-100 border-slate-200",
};

function SecTitle({ children }) {
  return <p className="text-[11px] font-bold uppercase tracking-wider text-[#012472] dark:text-[#80B3FF]">{children}</p>;
}

export default function PersonaFicha({ ficha, onRegistrarContacto, onVerHistorial }) {
  if (!ficha) return null;
  const { identidad: id, atencion: at, cobertura, observable: ob, ultimos_hechos, contactos } = ficha;
  const ac = at ? acentoPersona(at.prioridad) : null;

  return (
    <div data-testid="persona-ficha" className="space-y-4 pb-24 md:pb-0">
      {/* A. IDENTIDAD */}
      <div data-testid="persona-identidad">
        <div className="flex items-start gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#012472]/10 text-[#012472]"><User className="h-5 w-5" /></span>
          <div className="min-w-0">
            {/* Nombre COMPLETO legible en móvil: salta de línea, no se recorta. */}
            <p className="text-lg font-extrabold text-slate-800 dark:text-white break-words [overflow-wrap:anywhere]">{id.nombre || "(sin nombre)"}</p>
            <p className="text-xs text-slate-500">
              {id.edad ? `${id.edad} años · ` : ""}
              {/* C2 · modalidad con su PROCEDENCIA; un dato derivado no se declara como del centro. */}
              {etiquetaModalidad(id)}
              {id.idioma_preferido ? ` · Idioma: ${id.idioma_preferido}` : ""}
            </p>
          </div>
        </div>
        <p className="mt-1.5 flex items-start gap-1.5 text-sm text-slate-500"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="break-words [overflow-wrap:anywhere]">
            {[id.sitio_nombre, id.habitacion || id.zona_nombre,
              id.cama_etiqueta ? `Cama ${id.cama_etiqueta}` : null,
              id.direccion || id.ciudad].filter(Boolean).join(" · ") || "—"}
          </span></p>
      </div>

      {/* A-bis. CONTEXTO DE ATENCIÓN — C2: MISMO bloque informativo que la ficha de Sitios. */}
      {(ficha.instrucciones || ficha.atencion_contexto) && (
        <div data-testid="persona-indicaciones">
          <SecTitle>Contexto de atención</SecTitle>
          <div className="mt-1.5 rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-[#0B111E]">
            <BloqueContexto contexto={ficha.atencion_contexto} instrucciones={ficha.instrucciones}
              contextoHabitacion={ficha.contexto_habitacion} testId="radar-contexto-bloque" />
          </div>
        </div>
      )}

      {/* B. ATENCIÓN */}
      <div data-testid="persona-atencion">
        <SecTitle>Atención</SecTitle>
        {at ? (
          <div className="mt-1.5 rounded-xl p-3" style={{ backgroundColor: `${ac.hex}12`, border: `1px solid ${ac.hex}` }}>
            <div className="flex items-center gap-1.5">
              <Activity className="h-4 w-4" style={{ color: ac.hex }} />
              <p className="text-sm font-extrabold uppercase" style={{ color: ac.hex }}>{at.verbo}</p>
            </div>
            <p className="mt-1 text-xs font-bold" style={{ color: ac.hex }}>{at.prioridad_label} · {at.estado_label}</p>
            <p className="mt-0.5 text-xs text-slate-500">
              {at.en_atencion ? `${at.responsable || "Central Galarya"}${at.operador ? ` · ${at.operador}` : ""}` : "Pendiente de tomar"}
            </p>
          </div>
        ) : (
          <p data-testid="persona-sin-atencion" className="mt-1.5 rounded-lg bg-slate-50 dark:bg-[#0B111E] px-3 py-2 text-sm font-medium text-slate-500">Sin atención pendiente</p>
        )}
      </div>

      {/* C. COBERTURA — ANTES que lo observable */}
      <div data-testid="persona-cobertura">
        <SecTitle>Cobertura</SecTitle>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {cobertura.length === 0 && <span className="text-sm text-slate-400">Sin capacidades críticas declaradas.</span>}
          {cobertura.map((c, i) => {
            const tone = TONE[COBERTURA_TONE[c.estado] || "muted"];
            const Icon = (c.estado === "operativa" || c.estado === "protegida") ? ShieldCheck : c.estado === "sin_cobertura" ? ShieldOff : ShieldAlert;
            return (
              <span key={i} data-testid={`persona-cob-${c.capacidad}`} className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-semibold ${tone}`}>
                <Icon className="h-3.5 w-3.5" /> {c.capacidad}: {COBERTURA_LABEL[c.estado] || c.estado}
              </span>
            );
          })}
        </div>
      </div>

      {/* D. OBSERVABLE — cada dato con su propia antigüedad; nunca infiere normalidad */}
      <div data-testid="persona-observable">
        <SecTitle>Observable</SecTitle>
        {ob.compartida && (
          <p data-testid="persona-compartida" className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-medium text-slate-500 dark:border-[#1E2D4A] dark:bg-[#0B111E]">
            <UsersRound className="h-3.5 w-3.5 shrink-0" /> No atribuible individualmente · habitación compartida
          </p>
        )}
        {!ob.compartida && ob.items.length === 0 && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-400"><Radio className="h-3.5 w-3.5" /> Sin lectura disponible</p>
        )}
        {!ob.compartida && ob.items.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {ob.items.map((it, i) => (
              <li key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
                  {it.label === "Frecuencia cardiaca" ? <HeartPulse className="h-3.5 w-3.5 text-slate-400" /> : <Radio className="h-3.5 w-3.5 text-slate-400" />}
                  {it.label}: <b className="font-semibold">{it.valor}</b>
                </span>
                <span className="tabular-nums text-xs text-slate-400">{it.hace || "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* E. ÚLTIMOS HECHOS */}
      <div data-testid="persona-hechos">
        <div className="flex items-center justify-between">
          <SecTitle>Últimos hechos</SecTitle>
          {onVerHistorial && <button data-testid="persona-ver-historial" onClick={onVerHistorial} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0891A6] hover:underline"><History className="h-3.5 w-3.5" /> Ver historial de casos y eventos</button>}
        </div>
        {ultimos_hechos.length === 0 ? (
          <p className="mt-1.5 text-sm text-slate-400">Sin hechos recientes registrados.</p>
        ) : (
          <ul className="mt-1.5 space-y-1.5">
            {ultimos_hechos.map((h, i) => (
              <li key={i} data-testid={`persona-hecho-${i}`} data-clase={h.clase} className="flex items-start justify-between gap-2 rounded-lg bg-slate-50 dark:bg-[#0B111E] px-2.5 py-1.5">
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 leading-snug" title={h.label}>
                    {h.clase === "contacto" ? <Phone className="mr-1 inline h-3 w-3 text-[#0891A6]" /> : null}{h.label}
                  </span>
                  {h.sub && <span className="block text-xs text-slate-400 leading-snug">{h.sub}</span>}
                </span>
                <span className="shrink-0 tabular-nums text-xs text-slate-400" title={h.hora ? new Date(h.hora).toLocaleString("es-MX") : ""}>{h.hace || "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* E-bis. PLAN VIGENTE — MISMO bloque compartido que la ficha de Sitios (sólo lectura). */}
      {ficha.plan_vigente !== undefined && (
        <div data-testid="persona-plan-vigente">
          <SecTitle>Plan de respuesta</SecTitle>
          {/* P4 · presentación COMPLETA del plan también en Radar: nada se omite por espacio. */}
          <div className="mt-1.5">
            <BloquePlan plan={ficha.plan_vigente} testId="radar-plan-bloque" />
          </div>
        </div>
      )}

      {/* E-ter. EQUIPOS Y EJES — mismos bloques autorizados que Sitios. */}
      {ficha.dispositivos !== undefined && (
        <div data-testid="persona-equipos">
          <SecTitle>Cobertura técnica</SecTitle>
          <div className="mt-1.5"><BloqueEquipos dispositivos={ficha.dispositivos} testId="radar-equipos-bloque" /></div>
        </div>
      )}
      {ficha.readiness !== undefined && (
        <div data-testid="persona-ejes">
          <SecTitle>Preparación</SecTitle>
          <div className="mt-1.5"><BloqueEjes readiness={ficha.readiness} identidad={id} testId="radar-ejes-bloque" /></div>
        </div>
      )}

      {/* F. CONTACTOS / ACCESO — GC92: la burbuja flotante de alertas (fixed bottom, z-55) tapaba
          este bloque en móvil. El contenedor reserva carril inferior; GlobalAlertLayer no se toca.
          P5 · el bloque se renderiza SIEMPRE: él declara «no entregado», «vacío» o «error». */}
      <div data-testid="persona-contactos">
        <SecTitle>Contactos de atención</SecTitle>
        <div className="mt-1.5"><BloqueContactos contactos={contactos} testId="radar-contactos-bloque" /></div>
      </div>

      {onRegistrarContacto && (
        <button data-testid="persona-registrar-contacto" onClick={onRegistrarContacto}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-[#01CBE3] bg-[#01CBE3]/10 px-3 py-2 text-sm font-bold text-[#0891A6] hover:bg-[#01CBE3]/20">
          <PlusCircle className="h-4 w-4" /> Registrar contacto
        </button>
      )}
    </div>
  );
}
